import { cn, formatters, LongString, parseName } from '@nexelem/utils';
import { useCustomTranslation } from '../../../../../hooks';
import { KanbanTaskView, TaskStatus } from '@nexelem/graphne-api';
import { clsx } from 'clsx';
import { AVAILABLE_QUERY_PARAMS, useProductionPlanQueryParams } from '../../hooks';
import { MultipleTableAvatar } from '../../../../molecules/MultipleTableAvatar';
import { KanbanTime } from './KanbanTime';
import { CUSTOM_ICONS } from '@nexelem/icons';
import { getStatusColor } from './KanbanColumnHeader';
import { PropsWithChildren } from 'react';
import { KanbanStatusRow } from './KanbanStatusRow';
import { toggleTaskDetailsDrawer } from '../../../../../pages/generic/Products/components/TaskDetailDrawer/TaskDetailsDrawer';
import { GanttUtils } from '../../utils';
import { LocateProductionTask } from '../../Gantt/hooks';
import { TaskMainValueOptions, useProductionPlanSettings } from '../../components';
import { isSearchEmphasizedSelector, isSearchSelectedSelector, useProductionPlanSearchStore } from '../../store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping } from '@fortawesome/pro-light-svg-icons';

export type KanbanTaskProps = {
  task: KanbanTaskView;
  className?: string;
};

export const KanbanTask = ({ task, className }: KanbanTaskProps) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan');

  const showOnTaskSettings = useProductionPlanSettings((state) => state.kanban.showOnTask);
  const mainValue = useProductionPlanSettings((state) => state.taskMainValue);
  const kanbanTaskMapper = useKanbanTaskMapper();
  const { queryParams, setQueryParams } = useProductionPlanQueryParams();

  const {
    id,
    productionLotTags = [],
    orders,
    productName,
    status,
    name,
    requiresAcceptance,
    start,
    end,
    priority,
    summary,
    dueDate,
    isBehindSchedule,
    wasPaused,
    hasActiveDowntime,
    queueId,
  } = task;
  const {
    progressPercent,
    isOverdue,
    progressWithUnit,
    avatars,
    comments,
    hasError,
    exceededAmount,
    productionOrderNumber,
    productionOrderExternalNumber,
  } = kanbanTaskMapper(task, status);

  const openDetailsDrawer = () => {
    const isOpen = toggleTaskDetailsDrawer({
      taskId: id.toString(),
    });

    setQueryParams([
      {
        key: AVAILABLE_QUERY_PARAMS.taskId,
        value: isOpen ? id : undefined,
      },
      {
        key: AVAILABLE_QUERY_PARAMS.queueId,
        value: isOpen ? queueId : undefined,
      },
    ]);
  };

  const isActive = queryParams.taskId === id;
  const isSearchEmphasized = useProductionPlanSearchStore(isSearchEmphasizedSelector([id]));
  const isSearchSelected = useProductionPlanSearchStore(isSearchSelectedSelector([id]));

  return (
    <div
      className={cn(
        'flex cursor-pointer justify-between rounded-xs border border-border-container bg-surface-container-lighter p-4 hover:border-border-hover-lighter hover:bg-surface-hover-lighter',
        priority && 'border-2 border-border-brand-lighter hover:border-border-brand-lighter',
        isOverdue && 'border-2 border-border-status-rose hover:border-border-status-rose',
        hasError && 'border-2 border-border-status-red hover:border-border-status-red',
        wasPaused && 'group pause border-border-disabled bg-surface-disabled',
        className,
        isActive &&
          'border-2 border-border-focus-keyboard bg-surface-focus-lighter/90 shadow-medium hover:border-border-focus-keyboard',
        'group-[.disabled]:border-border-disabled group-[.disabled]:bg-surface-disabled',
        isSearchEmphasized &&
          'border-2 border-icon-dark bg-surface-status-node-search hover:border-icon-dark hover:bg-surface-status-node-search',
        isSearchSelected && 'animate-node-pulse',
      )}
      onClick={openDetailsDrawer}
      {...LocateProductionTask.addNodeSelector(id)}
    >
      <div className="flex w-full flex-col gap-y-2 pt-1">
        <KanbanStatusRow
          hasActiveDowntime={hasActiveDowntime}
          hasError={hasError}
          isBehindSchedule={isBehindSchedule}
          isOverdue={isOverdue}
          priority={priority}
          productionLotTags={productionLotTags}
          requiresAcceptance={requiresAcceptance}
          showProductionLotTags={showOnTaskSettings.productionTags}
          status={status}
          wasPaused={wasPaused}
        />
        <div className="flex justify-between">
          <div className="flex items-center">
            {showOnTaskSettings.assignedWorkers && !!avatars?.length && (
              <MultipleTableAvatar avatars={avatars} className="mr-2" />
            )}
            <span className={clsx(getStatusColor(status), 'mr-1 text-s16')}>{progressWithUnit}</span>
            {exceededAmount && <CUSTOM_ICONS.SortUp className="text-icon-darker" />}
          </div>
          {progressPercent && <span className={clsx(getStatusColor(status), 'text-s16')}>{progressPercent}</span>}
        </div>
        <div>
          <LongString
            className={clsx('text-t20-sb', 'group-[.disabled]:text-text-disabled', 'group-[.pause]:text-text-disabled')}
          >
            {mainValue === TaskMainValueOptions.ID ? name : productName}
          </LongString>
          <div className="text-s16 text-text-secondary-darker">
            {mainValue === TaskMainValueOptions.ID ? productName : name}
          </div>
        </div>

        {showOnTaskSettings.productionOrderNumber && productionOrderNumber && (
          <LabelContainer>
            {tWrapper('productionOrderNumber')} {productionOrderNumber}
          </LabelContainer>
        )}

        {showOnTaskSettings.productionOrderExternalNumber && productionOrderExternalNumber && (
          <LabelContainer>
            {tWrapper('productionOrderExternalNumber')} {productionOrderExternalNumber}
          </LabelContainer>
        )}

        {comments.length > 0 && (
          <div className="flex items-center gap-2 rounded-[2px] border border-border-container bg-surface-primary p-2">
            <CUSTOM_ICONS.Comment className="text-icon-darker" />
            <div className="flex flex-col text-s16">
              {comments.map((comment, index) => (
                <span key={index}>{comment}</span>
              ))}
            </div>
          </div>
        )}

        {showOnTaskSettings.technology && summary && (
          <div className="text-s16 text-text-secondary-darker">{summary}</div>
        )}

        {showOnTaskSettings.salesOrderIdClient &&
          orders?.map(({ supplierOrderNumber, buyerName, orderId }) => (
            <LabelContainer className="flex flex-wrap items-center gap-1 uppercase" key={orderId}>
              {supplierOrderNumber && (
                <>
                  <FontAwesomeIcon
                    className={clsx('min-h-4 min-w-4 group-[.disabled]:text-icon-disabled')}
                    icon={faCartShopping}
                  />
                  <span>{supplierOrderNumber}</span>
                </>
              )}
              <span>{buyerName}</span>
            </LabelContainer>
          ))}

        <KanbanTime dueDate={dueDate} end={end} isOverdue={isOverdue} start={start} status={status} />
      </div>
    </div>
  );
};

const LabelContainer = ({ children, className }: PropsWithChildren<{ className?: string }>) => (
  <span
    className={clsx(
      'self-start rounded-xs border border-border-container bg-surface-container px-2 py-1 text-text-secondary-darker',
      'group-[.disabled]:border-border-container group-[.disabled]:bg-surface-container group-[.disabled]:text-text-disabled',
      'group-[.pause]:border-border-disabled group-[.pause]:bg-surface-disabled group-[.pause]:text-text-disabled',
      'text-s11-sb',
      className,
    )}
  >
    {children}
  </span>
);

const formatPercents = (value: number) =>
  formatters.number.format(value, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'percent',
  });

export const formatUnit = (value: number) =>
  formatters.number.format(value, {
    maximumFractionDigits: 4,
    minimumFractionDigits: 0,
  });

const useKanbanTaskMapper = () => (task: KanbanTaskView, status?: string) => {
  const {
    progress: { current, totalAmount },
    errors,
    softResourceAssignments,
    orders = [],
    externalId,
    productionOrderDetails,
  } = task;

  const calculatedProgress = (current || 0) / (totalAmount.amount || 0) || 0;

  const shouldShowFullProgress = status !== TaskStatus.Pending || current > 0;
  const progressWithUnit = shouldShowFullProgress
    ? `${formatUnit(current)} / ${formatUnit(totalAmount.amount)} ${totalAmount.unit.name}`
    : `${formatUnit(totalAmount.amount)} ${totalAmount.unit.name}`;

  const progressPercent = shouldShowFullProgress && formatPercents(calculatedProgress);

  return {
    isOverdue: GanttUtils.hasOverdueError(errors),
    hasError: GanttUtils.hasCriticalError(errors),
    progressPercent,
    progressWithUnit,
    avatars: softResourceAssignments?.map(({ name }) => parseName(name)),
    comments: orders?.map(({ comment }) => comment).filter((comment): comment is string => !!comment),
    exceededAmount: current > totalAmount.amount,
    externalId: (externalId as { lotNumber?: string })?.lotNumber,
    productionOrderNumber: productionOrderDetails?.number,
    productionOrderExternalNumber: productionOrderDetails?.externalId,
  };
};
